import { useEffect, useState } from 'react';

/**
 * The one breakpoint the app's responsive layouts pivot on — Tailwind's `md`,
 * so a component's JS branch and its `md:` classes can never disagree.
 */
export const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

const readMatch = (): boolean =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? true // no matchMedia (SSR, jsdom): assume the desktop arrangement
    : window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

/**
 * Whether the viewport is at or above the desktop breakpoint, kept in sync as
 * the window resizes. Use it only where the layout genuinely needs a JS branch
 * (mounting different trees); prefer `md:` classes for pure styling.
 */
export const useIsDesktop = (): boolean => {
  const [desktop, setDesktop] = useState(readMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const update = () => setDesktop(query.matches);
    update();
    // addListener is the deprecated form, still needed by older Safari.
    if (query.addEventListener) query.addEventListener('change', update);
    else query.addListener(update);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', update);
      else query.removeListener(update);
    };
  }, []);

  return desktop;
};

export default useIsDesktop;
